import React, { useRef, useState } from "react";
import { Upload, File as FileIcon, X, Loader2, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { toast } from "sonner";

// Uploads files immediately to object storage and reports file ids via onChange.
export default function FileUpload({ onChange, accept = ".pdf,.jpg,.jpeg,.png,.webp,.heic", testId = "file-upload" }) {
  const { t } = useLang();
  const inputRef = useRef(null);
  const [files, setFiles] = useState([]); // {id, name, uploading}
  const [busy, setBusy] = useState(false);

  const emit = (list) => onChange && onChange(list.filter((f) => f.id).map((f) => f.id));

  const handleFiles = async (fileList) => {
    const arr = Array.from(fileList);
    setBusy(true);
    for (const file of arr) {
      const temp = { tempId: Math.random().toString(36), name: file.name, uploading: true };
      setFiles((prev) => [...prev, temp]);
      try {
        const form = new FormData();
        form.append("file", file);
        const res = await api.post("/uploads", form, { headers: { "Content-Type": "multipart/form-data" } });
        setFiles((prev) => {
          const next = prev.map((f) => (f.tempId === temp.tempId ? { ...f, id: res.data.id, uploading: false } : f));
          emit(next);
          return next;
        });
      } catch (e) {
        const code = e.response?.data?.detail;
        const msg = { pdf_empty: t.upload.errEmpty, file_empty: t.upload.errEmpty, pdf_corrupt: t.upload.errCorrupt }[code];
        toast.error(msg ? `${file.name}: ${msg}` : "Upload failed: " + file.name, msg ? { duration: 8000 } : undefined);
        setFiles((prev) => prev.filter((f) => f.tempId !== temp.tempId));
      }
    }
    setBusy(false);
  };

  const remove = (tempId) => {
    setFiles((prev) => {
      const next = prev.filter((f) => f.tempId !== tempId);
      emit(next);
      return next;
    });
  };

  return (
    <div data-testid={testId}>
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        className="w-full border-2 border-dashed border-stone-300 rounded-xl p-6 text-center hover:border-[#1E3A2F] hover:bg-stone-50 transition-colors"
        data-testid={`${testId}-trigger`}
      >
        <Upload className="w-6 h-6 mx-auto text-stone-400" />
        <p className="mt-2 text-sm font-medium text-stone-700">{t.upload.drop}</p>
        <p className="text-xs text-stone-400 mt-0.5">{t.upload.types}</p>
      </button>
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={accept}
        className="hidden"
        onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }}
        data-testid={`${testId}-input`}
      />
      {files.length > 0 && (
        <ul className="mt-3 space-y-2">
          {files.map((f) => (
            <li key={f.tempId} className="flex items-center gap-2 bg-stone-50 border border-stone-200 rounded-lg px-3 py-2 text-sm">
              <FileIcon className="w-4 h-4 text-stone-400 shrink-0" />
              <span className="flex-1 truncate text-stone-700">{f.name}</span>
              {f.uploading ? (
                <Loader2 className="w-4 h-4 animate-spin text-stone-400" />
              ) : (
                <CheckCircle2 className="w-4 h-4 text-emerald-600" />
              )}
              <button type="button" onClick={() => remove(f.tempId)} className="text-stone-400 hover:text-stone-700" aria-label="remove">
                <X className="w-4 h-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
